import { IconFont } from '@/components'
import type { ITableTag, ITag } from '@/services/custom/extractConfig'
import { removeTags } from '@/services/custom/extractConfig'
import { Activity } from '@ivliu/react-offscreen'
import { useMutation, useSearchParams } from '@umijs/max'
import type { FormInstance, FormListFieldData, ModalFuncProps } from 'antd'
import { App, Badge, Button, Form } from 'antd'
import classNames from 'classnames'
import type { FC } from 'react'
import { useContext, useEffect, useState } from 'react'
import { ReactSortable } from 'react-sortablejs'
import { readonlyContext } from '../../../context'
import { useFormDirty } from '../../stores/useFormDirty'
import { Fallback } from './Fallback'
import { InputWithRemoveAction } from './InputWithRemoveAction'
import { TableCreationButton } from './TableCreationButton'
import { TableTitleEditor } from './TableTitleEditor'
import { Tabs } from './Tabs'
import type { FormListFieldDataWithId, IFormValue } from './type'
import { FIELD_TYPES } from '../../type'

const { useWatch, List: FormList, Item: FormItem } = Form
const { useApp } = App
const INITIAL_VALUES: IFormValue = {
  text_tags: [],
  table_tags: [],
  seal_tags: [],
  hand_writing_tags: [],
}

export const TabsForm: FC<{
  form: FormInstance<IFormValue>
  // onSubmit: (values: IFormValue) => void;
  initialValues?: IFormValue
}> = props => {
  // props
  const { form, initialValues = INITIAL_VALUES } = props

  // 是否编辑过表单或画布 sharestate
  const [isDirty, setDirty] = useFormDirty()

  // state
  const [openMap, setOpenMap] = useState<Record<number, boolean>>({})
  // context
  const readonly = useContext(readonlyContext)
  // search params
  const searchParams = useSearchParams()[0]
  const templateCode = searchParams.get('template-code')!
  // app
  const { modal } = useApp()
  // mutation
  const { mutateAsync: removeAsync } = useMutation(removeTags)
  /**文本字段 */
  const length$1 =
    useWatch(values => values.text_tags, form)?.length ?? initialValues.text_tags.length
  /**表格字段 */
  const length$2 =
    useWatch(values => values.table_tags, form)?.length ?? initialValues.table_tags.length
  /**印章字段 */
  const length$3 =
    useWatch(values => values.seal_tags, form)?.length ?? initialValues.seal_tags.length
  /**手写体字段 */
  const length$4 =
    useWatch(values => values.hand_writing_tags, form)?.length ??
    initialValues.hand_writing_tags.length
  // methods
  const createTag = (
    field: FormListFieldData,
    onRemove: (index: number | number[]) => void,
    autoScrollIntoView = false,
  ) => {
    const { name, key } = field

    return (
      <FormItem
        className='group'
        name={[name, 'tag_name']}
        normalize={(v: string) => v.trim()}
        rules={[
          { required: true, message: '请输入字段名称' },
          // {
          //   max: 32,
          //   message: '长度需要小于32个字符',
          // },
        ]}
        key={key}>
        <InputWithRemoveAction
          allowClear
          placeholder='请输入字段名称'
          maxLength={32}
          autoScrollIntoView={autoScrollIntoView}
          onRemove={() => onRemove(name)}
        />
      </FormItem>
    )
  }
  const createDialog = (props: ModalFuncProps) =>
    modal.confirm({
      title: '提示',
      okText: '确认删除',
      okButtonProps: {
        danger: true,
      },
      ...props,
    })

  useEffect(() => {
    form.setFieldsValue(initialValues)
  }, [form, initialValues])

  return (
    <Form
      form={form}
      // initialValues={initialValues}
      disabled={readonly}
      onFieldsChange={() => {
        if (isDirty) return
        setDirty(true)
      }}
      // onFinish={onSubmit}
    >
      <Tabs
        items={[
          {
            key: FIELD_TYPES.FIELD,
            label: <Badge offset={[4, -4]}>文本字段 ({length$1})</Badge>,
            forceRender: true,
            children: (
              <section className='px-56 pt-4'>
                <p className='mb-4 text-secondary-2 text-xs'>
                  什么是文本字段？文本字段是需要从文件中抽取的一段文字描述。比如合同中的甲方名称、交付日期、银行卡号、合同风险等。可以使用语义规则抽取节点来抽取满足某种模式的文本字段、大模型抽取节点来抽取需要综合上下文信息总结得到的文本信息，详见模版配置的节点说明。
                </p>
                <FormList name='text_tags'>
                  {(fields, { add, remove }) => {
                    const initialTag = { tag_name: '' } as ITag
                    if (fields.length === 0) {
                      return (
                        <Fallback>
                          <p className='text-sm main'>暂无字段，请添加字段</p>
                          <Button
                            type='dashed'
                            className='mt-4'
                            icon={<IconFont type='icon-tianjia1' />}
                            onClick={() => add(initialTag)}>
                            添加字段
                          </Button>
                        </Fallback>
                      )
                    }
                    return (
                      <>
                        {fields.map(field =>
                          createTag(
                            field,
                            index => {
                              const code = form.getFieldValue([
                                'text_tags',
                                index as number,
                                'tag_code',
                              ])
                              if (code) {
                                createDialog({
                                  content: '删除后，该字段将不可找回，确认删除吗？',
                                  onOk: () =>
                                    removeAsync({
                                      template_code: templateCode,
                                      tag_code: code,
                                    }).then(() => remove(index)),
                                })
                              } else {
                                remove(index)
                              }
                            },
                            true,
                          ),
                        )}
                        <Button
                          block
                          type='dashed'
                          className='bottom-0 sticky'
                          icon={<IconFont type='icon-tianjia1' />}
                          onClick={() => add(initialTag)}>
                          添加字段
                        </Button>
                      </>
                    )
                  }}
                </FormList>
              </section>
            ),
          },
          {
            key: FIELD_TYPES.TABLE,
            label: <Badge offset={[4, -4]}>表格字段 ({length$2})</Badge>,
            forceRender: true,
            children: (
              <section className='px-48 pt-4'>
                <p className='mb-4 text-secondary-2 text-xs'>
                  什么是表格字段？表格字段是需要从文件中抽取的一张二维表，比如订货单中商品信息表；一个表格字段需要配置多个列字段，比如商品信息表中的商品名称、单价、数量等。表格类型的字段最终的抽取结果是一张结构化的二维表。可以使用表格抽取节点来对指定的列字段进行抽取，详见模版配置的节点说明。
                </p>
                <FormList name='table_tags'>
                  {(fields, { add, remove }) => {
                    const initialTableData = {
                      table_name: `表格 ${fields.length + 1}`,
                      tags: [{ tag_name: '' }],
                    } as ITableTag

                    if (fields.length === 0) {
                      return (
                        <Fallback>
                          <p className='text-main text-sm'>暂无表格字段</p>
                          <p className='text-secondary-2 text-xs'>请先添加表格，再配置表格字段</p>
                          <Button
                            type='dashed'
                            className='mt-4'
                            icon={<IconFont type='icon-biaogeshibie' />}
                            onClick={() => add(initialTableData)}>
                            添加表格
                          </Button>
                        </Fallback>
                      )
                    }
                    return (
                      <>
                        {fields.map(field => {
                          const { key, name } = field
                          const open = openMap[name] ?? true
                          const tableCode = form.getFieldValue(['table_tags', name, 'table_code'])

                          return (
                            <FormItem
                              className={classNames(
                                'px-4 bg-main-bg rounded-md transition hover:shadow-md',
                                { 'pb-4': open },
                              )}
                              name={name}
                              key={key}>
                              <FormItem
                                className='mb-0'
                                name={[name, 'table_name']}
                                rules={[
                                  {
                                    required: true,
                                    message: '表格名称不得为空',
                                    validateTrigger: 'submit',
                                  },
                                ]}>
                                <TableTitleEditor
                                  open={open}
                                  onOpenValueChange={open =>
                                    setOpenMap({
                                      ...openMap,
                                      [name]: open,
                                    })
                                  }
                                  onRemove={() => {
                                    if (tableCode) {
                                      createDialog({
                                        content:
                                          '删除后，该表格及表格下字段将不可找回，确认删除吗？',
                                        onOk: () =>
                                          removeAsync({
                                            template_code: templateCode,
                                            table_code: tableCode,
                                          }).then(() => remove(name)),
                                      })
                                    } else {
                                      remove(name)
                                    }
                                  }}
                                />
                              </FormItem>
                              <Activity mode={open ? 'visible' : 'hidden'}>
                                <FormList name={[name, 'tags']}>
                                  {(fields, { add, remove, move }) => (
                                    <ReactSortable
                                      list={fields.map(field => {
                                        const _field = field as FormListFieldDataWithId
                                        _field.id = field.name
                                        return _field
                                      })}
                                      setList={() => null}
                                      animation={150}
                                      onEnd={e => {
                                        const { oldIndex, newIndex } = e

                                        move(oldIndex!, newIndex!)
                                      }}>
                                      {fields.map(field =>
                                        createTag(field, index => {
                                          const code = form.getFieldValue([
                                            'table_tags',
                                            name,
                                            'tags',
                                            index as number,
                                            'tag_code',
                                          ])
                                          if (code) {
                                            createDialog({
                                              content: '删除后，该字段将不可找回，确认删除吗？',
                                              onOk: () =>
                                                removeAsync({
                                                  template_code: templateCode,
                                                  table_code: tableCode,
                                                  tag_code: code,
                                                }).then(() => remove(index)),
                                            })
                                          } else {
                                            remove(index)
                                          }
                                        }),
                                      )}
                                      <TableCreationButton
                                        icon={<IconFont type='icon-tianjia1' />}
                                        onClick={() => add({ tag_name: '' })}
                                      />
                                    </ReactSortable>
                                  )}
                                </FormList>
                              </Activity>
                            </FormItem>
                          )
                        })}
                        <Button
                          block
                          type='dashed'
                          className='bottom-0 sticky'
                          icon={<IconFont type='icon-biaogeshibie' />}
                          onClick={() => add(initialTableData)}>
                          添加表格
                        </Button>
                      </>
                    )
                  }}
                </FormList>
              </section>
            ),
          },
          {
            key: FIELD_TYPES.SEAL,
            label: <Badge offset={[4, -4]}>印章字段 ({length$3})</Badge>,
            forceRender: true,
            children: (
              <section className='px-56 pt-4'>
                <p className='mb-4 text-secondary-2 text-xs'>
                  什么是印章字段？印章字段是需要从文件中抽取的印章，比如合同中的甲方章、乙方章。印章类型字段的抽取信息包含印章的颜色、形状、印章文字等。可以使用印章抽取节点来对指定页或者指定位置的印章信息进行抽取，详见模版配置的节点说明。
                </p>

                <FormList name='seal_tags'>
                  {(fields, { add, remove }) => {
                    const initialTag = { tag_name: '' } as ITag
                    if (fields.length === 0) {
                      return (
                        <Fallback>
                          <p className='text-sm main'>暂无字段，请添加字段</p>
                          <Button
                            type='dashed'
                            className='mt-4'
                            icon={<IconFont type='icon-tianjia1' />}
                            onClick={() => add(initialTag)}>
                            添加字段
                          </Button>
                        </Fallback>
                      )
                    }
                    return (
                      <>
                        {fields.map(field =>
                          createTag(
                            field,
                            index => {
                              const code = form.getFieldValue([
                                'seal_tags',
                                index as number,
                                'tag_code',
                              ])
                              if (code) {
                                createDialog({
                                  content: '删除后，该字段将不可找回，确认删除吗？',
                                  onOk: () =>
                                    removeAsync({
                                      template_code: templateCode,
                                      tag_code: code,
                                    }).then(() => remove(index)),
                                })
                              } else {
                                remove(index)
                              }
                            },
                            true,
                          ),
                        )}
                        <Button
                          block
                          type='dashed'
                          className='bottom-0 sticky'
                          icon={<IconFont type='icon-tianjia1' />}
                          onClick={() => add(initialTag)}>
                          添加字段
                        </Button>
                      </>
                    )
                  }}
                </FormList>
              </section>
            ),
          },
          {
            key: FIELD_TYPES.HAND_WRITING,
            label: <Badge offset={[4, -4]}>手写体字段 ({length$4})</Badge>,
            forceRender: true,
            children: (
              <section className='px-56 pt-4'>
                <p className='mb-4 text-secondary-2 text-xs'>
                  什么是手写体字段？手写体字段是需要从文件中抽取的手写体信息，比如合同中的甲方签字、乙方签字，审核表中的审核人签字、审核日期。手写字类型字段的抽取信息手写体图片信息。可以使用手写体抽取节点来对指定页或者指定位置的手写体信息进行抽取，详见模版配置的节点说明。
                </p>
                <FormList name='hand_writing_tags'>
                  {(fields, { add, remove }) => {
                    const initialTag = { tag_name: '' } as ITag
                    if (fields.length === 0) {
                      return (
                        <Fallback>
                          <p className='text-sm main'>暂无字段，请添加字段</p>
                          <Button
                            type='dashed'
                            className='mt-4'
                            icon={<IconFont type='icon-tianjia1' />}
                            onClick={() => add(initialTag)}>
                            添加字段
                          </Button>
                        </Fallback>
                      )
                    }
                    return (
                      <>
                        {fields.map(field =>
                          createTag(
                            field,
                            index => {
                              const code = form.getFieldValue([
                                'hand_writing_tags',
                                index as number,
                                'tag_code',
                              ])
                              if (code) {
                                createDialog({
                                  content: '删除后，该字段将不可找回，确认删除吗？',
                                  onOk: () =>
                                    removeAsync({
                                      template_code: templateCode,
                                      tag_code: code,
                                    }).then(() => remove(index)),
                                })
                              } else {
                                remove(index)
                              }
                            },
                            true,
                          ),
                        )}
                        <Button
                          block
                          type='dashed'
                          className='bottom-0 sticky'
                          icon={<IconFont type='icon-tianjia1' />}
                          onClick={() => add(initialTag)}>
                          添加字段
                        </Button>
                      </>
                    )
                  }}
                </FormList>
              </section>
            ),
          },
        ]}
      />
    </Form>
  )
}
